// @ts-check
// Resumo do público em linguagem simples (contrato 09 de público, seção 9): uma lista de frases, como "Fizeram visita",
// "Não matriculados", "Unidade: Centro", "Família / CEP: contém 13025" e "Mais 1 condição". Usado no cartão de entrada
// do fluxo, no modal "Salvar e ativar" e na lista de campanhas.
import { getFilterDisplayValue, getSubFieldLabel, operandNeedsValue } from './view-filters.js';
import { t, translateMetadataLabel } from '../i18n/index.js';

/**
 * @typedef {import('../../../shared/audiences/audience-definition.js').AudienceDefinition} AudienceDefinition
 * @typedef {import('./field-metadata.js').FieldMetadata} FieldMetadata
 */

const DEFAULT_MAXIMUM_CONDITIONS = 3;

/**
 * Frase de uma regra: "Campo: valor", "Campo: não contém valor", "Alunos / Curso: algum é Ensino Médio".
 * @param {AudienceDefinition['filters'][number]} filter @param {(id: string) => FieldMetadata | undefined} getField
 * @returns {string | null}
 */
export function describeAudienceRule(filter, getField) {
  const field = getField(filter.fieldMetadataId);
  if (!field) return null;
  const target = filter.targetFieldMetadataId ? getField(filter.targetFieldMetadataId) : undefined;
  const parts = [translateMetadataLabel(field.label)];
  if (target) parts.push(translateMetadataLabel(target.label));
  if (filter.subFieldName) parts.push(getSubFieldLabel(filter.subFieldName));
  const isOneToMany = field.type === 'RELATION' && field.relation?.type === 'ONE_TO_MANY';
  const operandText = isOneToMany ? t(`audience.oneToMany.${filter.operand}`) : t(`filters.operands.${filter.operand}`).toLowerCase();
  const valueField = target ?? field;
  const value = operandNeedsValue(filter.operand) && (!isOneToMany || target) ? getFilterDisplayValue(/** @type {any} */ (filter), valueField) : '';
  return t('audience.summary.rule', { field: parts.join(' / '), operand: operandText, value }).trim();
}

/**
 * @param {AudienceDefinition} definition
 * @param {{ getField: (id: string) => FieldMetadata | undefined, views?: Array<{ id: string, name: string }>,
 *   usage?: 'AUDIENCE' | 'CONDITION', maximumConditions?: number }} options
 * @returns {string[]}
 */
export function summarizeAudience(definition, options) {
  /** @type {string[]} */
  const phrases = [];
  if (definition.viewId) {
    const view = options.views?.find((item) => item.id === definition.viewId);
    phrases.push(view ? t('audience.summary.view', { name: translateMetadataLabel(view.name) }) : t('audience.summary.viewMissing'));
  }
  if (definition.recordIds) phrases.push(t('audience.summary.recordIds', { count: definition.recordIds.length }));
  if (definition.presets.visit !== 'ANY') phrases.push(t(`audience.summary.visit.${definition.presets.visit}`));
  if (definition.presets.enrollment !== 'ANY') phrases.push(t(`audience.summary.enrollment.${definition.presets.enrollment}`));
  const defaultLost = options.usage === 'CONDITION' ? 'INCLUDE' : 'EXCLUDE';
  if (definition.presets.lost !== defaultLost) phrases.push(t(`audience.summary.lost.${definition.presets.lost}`));
  if (definition.anyFieldSearch.trim()) phrases.push(t('audience.summary.search', { text: definition.anyFieldSearch.trim() }));
  const rules = definition.filters.map((filter) => describeAudienceRule(filter, options.getField)).filter((phrase) => Boolean(phrase));
  const maximum = options.maximumConditions ?? DEFAULT_MAXIMUM_CONDITIONS;
  phrases.push(.../** @type {string[]} */ (rules.slice(0, maximum)));
  if (rules.length > maximum) phrases.push(t('audience.summary.more', { count: rules.length - maximum }));
  if (phrases.length === 0) phrases.push(t(options.usage === 'CONDITION' ? 'audience.summary.anyLead' : 'audience.summary.allLeads'));
  return phrases;
}
